import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { inactiveSelectionColor } from '@aline/common';
import { Box } from '@mui/material';
import {
  BoldPlugin,
  HeadingPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@platejs/basic-nodes/react';
import { CursorOverlayPlugin, useCursorOverlay } from '@platejs/selection/react';
import { TrailingBlockPlugin } from '@platejs/utils';
import clsx from 'clsx';
import { isHotkey } from 'is-hotkey';
import { HtmlPlugin, RangeApi } from 'platejs';
import {
  Plate,
  PlateContainer,
  PlateContent,
  useEditorSelector,
  usePlateEditor,
  useSelectionVersion,
} from 'platejs/react';
import { useMount } from 'react-use';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { AlineMarkdownEditorToolbar } from './AlineMarkdownEditorToolbar';
import { EmptyParagraphAaProvider } from './EmptyParagraphAaToggle';
import { LinkKit } from './plugins/LinkKit';
import { ListKit } from './plugins/ListKit';
import { BaseMarkdownPlugin } from './plugins/MarkdownPlugin';
import {
  MentionElementPlugin,
  MentionEntitiesContext,
  MentionEntityKit,
} from './plugins/mention-entity-kit';
import { ParagraphPlugin } from './plugins/ParagraphPlugin';
import { TableKit } from './plugins/TableKit';
import type { AlineMarkdownEditorProps, AlineMarkdownEditorRef } from './types';
import { getShouldShowEmptyParagraphAa } from './utils/empty-paragraph';
import { focusEditor } from './utils/focus';
import { getEditorMarkdown } from './utils/markdown';
import { openMentionCombobox } from './utils/open-mention-combobox';
import {
  deserializeMarkdownWithMentions,
  getIsEditorWithMarkdownApi,
  getMarkdownHasMentionType,
} from '../ChatEditor';

const useEditorStyles = makeStyles()(theme => ({
  container: {
    '&:focus-within': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 20,
    boxSizing: 'border-box',
    overflow: 'hidden',
    width: '100%',
  },
  // Floats the toolbar as a detached bar above the editor (Figma "Aa" popover).
  floatingToolbarWrap: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 10,
    bottom: 'calc(100% + 6px)',
    boxShadow: theme.shadows[3],
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    zIndex: 5,
  },
  textarea: {
    ...getTypographyForSx(theme.typography.body1),
    '& [data-slate-placeholder]': {
      fontStyle: 'normal',
      fontWeight: 'normal',
      textDecoration: 'none',
    },
    backgroundColor: 'transparent',
    border: 'none',
    boxSizing: 'border-box',
    color: theme.appColors.iconsType,
    display: 'block',
    margin: 0,
    outline: 'none',
    overflow: 'auto',
    padding: theme.spacing(1),
    resize: 'none',
    width: '100%',
  },
}));

/**
 * Simple, all-inclusive markdown editor component.
 * Provides a toolbar with all formatting buttons and a text area.
 */
export const AlineMarkdownEditor = forwardRef<AlineMarkdownEditorRef, AlineMarkdownEditorProps>(
  function AlineMarkdownEditor(
    {
      autoFocus = false,
      bottomContent,
      className,
      dataCy,
      disabled = false,
      extraPlugins = [],
      floatingToolbar = false,
      hideToolbar = false,
      maxHeight = 300,
      maxWidth,
      mentions,
      minHeight = 80,
      noBorder = false,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onPaste,
      onSubmit,
      placeholder = 'Type something...',
      readOnly = false,
      showToolbarOnSelection = false,
      style,
      submitDisabled = false,
      textareaStyle,
      toolbarStyle,
      value,
      width,
    },
    ref
  ) {
    const { classes } = useEditorStyles();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    /**
     * Toggled by the empty-paragraph Aa button. Cleared when the caret leaves a blank
     * paragraph or the selection expands so the format bar doesn't stick open.
     */
    const [emptyParagraphToolbarOpen, setEmptyParagraphToolbarOpen] = useState(false);

    /**
     * The plugin list keys off a boolean (not the mentions object) so inline config
     * props do not recreate the editor every render; the config itself flows through
     * MentionEntitiesContext below.
     */
    const mentionsEnabled = Boolean(mentions);
    const mentionsContextValue = useMemo(
      () =>
        mentions
          ? {
              allowAlineAiMention: mentions.allowAlineAiMention,
              allowEmailMentions: mentions.allowEmailMentions,
              allowFileUpload: mentions.allowFileUpload,
              allowIntakeFormMention: mentions.allowIntakeFormMention,
              documentId: mentions.documentId,
              enabledTypes: mentions.enabledTypes,
              filterUser: mentions.filterUser,
              quickTypes: mentions.quickTypes,
              workflowId: mentions.workflowId,
            }
          : null,
      [mentions]
    );
    const plugins = useMemo(
      () => [
        HtmlPlugin,
        ParagraphPlugin,
        BoldPlugin,
        ItalicPlugin,
        UnderlinePlugin,
        StrikethroughPlugin,
        HeadingPlugin,
        ...ListKit,
        ...LinkKit,
        ...TableKit,
        /**
         * Mention chips render in every editor so persisted mention links stay visible;
         * the @-trigger input and entity picker mount only when mentions are enabled.
         */
        MentionElementPlugin,
        ...(mentionsEnabled ? MentionEntityKit : []),
        // Cursor overlay for selection preservation (always rendered for consistent behavior).
        CursorOverlayPlugin.configure({
          render: {
            afterEditable: () => <CursorOverlay />,
          },
        }),
        // Trailing block plugin - ensures there's always a paragraph at the end.
        TrailingBlockPlugin.configure({
          options: {
            type: 'p',
          },
        }),
        // Markdown plugin (must come last for proper serialization).
        BaseMarkdownPlugin,
        // Extra plugins overriding base plugins.
        ...extraPlugins,
      ],
      [extraPlugins, mentionsEnabled]
    );
    const editor = usePlateEditor({
      plugins,
      value: [{ children: [{ text: '' }], type: 'p' }],
    });
    const handleEditorChange = useDebouncedCallback(() => {
      if (onChange) {
        onChange(getEditorMarkdown(editor));
      }
    }, 100);

    /**
     * Flush the pending onChange on unmount so the last sub-100ms of typing isn't dropped when the
     * editor is torn down (e.g. switching tasks / closing a panel mid-keystroke).
     */
    useEffect(() => () => handleEditorChange.flush(), [handleEditorChange]);

    useMount(() => {
      if (value && getIsEditorWithMarkdownApi(editor))
        editor.tf.setValue(deserializeMarkdownWithMentions(editor, value));
    });

    useImperativeHandle(ref, () => ({
      clear: () => {
        editor.tf.reset();
        focusEditor(editor, 'start');
      },
      focus: (position = 'preserve') => {
        focusEditor(editor, position);
      },
      getEditor: () => editor,
      getHasMentionType: mentionTypeName =>
        getIsEditorWithMarkdownApi(editor)
          ? getMarkdownHasMentionType(editor, getEditorMarkdown(editor), mentionTypeName)
          : false,
      getMarkdown: () => getEditorMarkdown(editor),
      getScrollContainer: () => scrollContainerRef.current,
      insertText: (text: string) => {
        editor.tf.insertText(text);
        focusEditor(editor, 'end');
      },
      openMentionPicker: () => openMentionCombobox(editor),
      setMarkdown: (markdown: string) => {
        if (getIsEditorWithMarkdownApi(editor))
          editor.tf.setValue(deserializeMarkdownWithMentions(editor, markdown));
      },
    }));

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Call custom handler first
      onKeyDown?.(e);
      if (e.defaultPrevented) return;

      // Handle Cmd+Enter for submit (only if not disabled)
      if (isHotkey('mod+enter', e.nativeEvent) && onSubmit && !submitDisabled) {
        e.preventDefault();
        onSubmit(getEditorMarkdown(editor));
      }
    };

    // Compute container styles with width/maxWidth overrides
    const containerStyle: React.CSSProperties = {
      ...(width !== undefined && { width }),
      ...(maxWidth !== undefined && { maxWidth }),
      ...(noBorder && { border: 'none', borderRadius: 0 }),
      // A floating toolbar is positioned relative to the container and must not be clipped.
      ...(floatingToolbar && { overflow: 'visible', position: 'relative' }),
      ...style,
    };

    const toolbarNode = (
      <AlineMarkdownEditorToolbar disabled={disabled || readOnly} style={toolbarStyle} />
    );
    const toolbar = !hideToolbar && toolbarNode;
    // Selection-raised toolbar only applies to the floating variant, when the explicit toolbar is
    // hidden, and while the editor is actually editable.
    const selectionToolbar =
      !toolbar && floatingToolbar && showToolbarOnSelection && !readOnly && !disabled;
    /**
     * Lets the shared paragraph renderer draw the Aa toggle inline on the blank
     * line the caret is in. Null for every other editor variant.
     */
    const emptyParagraphAaState = useMemo(
      () =>
        selectionToolbar
          ? { onOpenChange: setEmptyParagraphToolbarOpen, open: emptyParagraphToolbarOpen }
          : null,
      [emptyParagraphToolbarOpen, selectionToolbar]
    );

    return (
      <MentionEntitiesContext.Provider value={mentionsContextValue}>
        <EmptyParagraphAaProvider value={emptyParagraphAaState}>
          <Box className={clsx(classes.container, className)} style={containerStyle}>
            <Plate editor={editor} onChange={handleEditorChange}>
              {/* Toolbar — inline at the top, or floating above the card. */}
              {toolbar &&
                (floatingToolbar ? (
                  <div className={classes.floatingToolbarWrap}>{toolbar}</div>
                ) : (
                  toolbar
                ))}
              {selectionToolbar && (
                <FloatingFormatToolbar
                  className={classes.floatingToolbarWrap}
                  emptyParagraphToolbarOpen={emptyParagraphToolbarOpen}
                  onEmptyParagraphToolbarOpenChange={setEmptyParagraphToolbarOpen}
                >
                  {toolbarNode}
                </FloatingFormatToolbar>
              )}

              {/* Editor content */}
              <PlateContainer>
                <PlateContent
                  ref={scrollContainerRef}
                  autoFocus={autoFocus}
                  className={classes.textarea}
                  data-cy={dataCy}
                  placeholder={placeholder}
                  readOnly={readOnly || disabled}
                  style={{
                    maxHeight,
                    minHeight,
                    opacity: disabled ? 0.6 : 1,
                    ...textareaStyle,
                  }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  onKeyDown={handleKeyDown}
                  onPaste={onPaste}
                />
              </PlateContainer>
            </Plate>

            {/* Optional bottom content (e.g., custom toolbar buttons) */}
            {bottomContent}
          </Box>
        </EmptyParagraphAaProvider>
      </MentionEntitiesContext.Provider>
    );
  }
);

/** Shows selection highlight when editor loses focus. */
function CursorOverlay() {
  const { cursors } = useCursorOverlay();

  return (
    <>
      {cursors.flatMap(({ caretPosition, id, selectionRects }) => [
        ...selectionRects.map((rect, i) => (
          <div
            key={`${id}-${i}`}
            style={{
              background: inactiveSelectionColor.toString(),
              pointerEvents: 'none',
              position: 'absolute',
              zIndex: 10,
              ...rect,
            }}
          />
        )),
        caretPosition && (
          <div
            key={`${id}-caret`}
            style={{
              background: inactiveSelectionColor.toString(),
              pointerEvents: 'none',
              position: 'absolute',
              width: 2,
              zIndex: 10,
              ...caretPosition,
            }}
          />
        ),
      ])}
    </>
  );
}

/**
 * Renders its children (the toolbar) while either:
 * - the editor has an expanded (non-empty) text selection, or
 * - the empty-paragraph Aa toggle has opened the toolbar and the caret is still
 *   in a blank paragraph.
 *
 * Must be a child of <Plate>. Visibility is derived through useEditorSelector so
 * it tracks the *live* selection: collapsing the selection, typing after it, or
 * leaving a blank paragraph each hide the toolbar.
 */
function FloatingFormatToolbar({
  children,
  className,
  emptyParagraphToolbarOpen,
  onEmptyParagraphToolbarOpenChange,
}: {
  children: React.ReactNode;
  className: string;
  emptyParagraphToolbarOpen: boolean;
  onEmptyParagraphToolbarOpenChange: (open: boolean) => void;
}): JSX.Element | null {
  /**
   * Selection-only moves (arrow keys, click-to-place caret) bump
   * `versionSelection`, not `versionEditor`. Include it in both selectors' deps
   * so the toolbar follows the caret without requiring a content change.
   */
  const selectionVersion = useSelectionVersion();
  const showFromSelection = useEditorSelector(
    editor => {
      const selection = editor.selection;
      return Boolean(selection && RangeApi.isExpanded(selection) && !editor.api.isEmpty());
    },
    [selectionVersion]
  );
  const canShowFromAa = useEditorSelector(
    editor => getShouldShowEmptyParagraphAa(editor),
    [selectionVersion]
  );
  const show = showFromSelection || (emptyParagraphToolbarOpen && canShowFromAa);

  /**
   * Drop the Aa-opened state once the caret leaves a blank paragraph / the
   * document gains content, or the selection expands — otherwise the bar sticks.
   */
  useEffect(() => {
    if (!emptyParagraphToolbarOpen) return;
    if (showFromSelection || !canShowFromAa) onEmptyParagraphToolbarOpenChange(false);
  }, [
    canShowFromAa,
    emptyParagraphToolbarOpen,
    onEmptyParagraphToolbarOpenChange,
    showFromSelection,
  ]);

  if (!show) return null;

  return <div className={className}>{children}</div>;
}
